import { captureEvent } from "@Light/app/analytics";
import type {
  AddressSearchResponse,
  RequirementsPatch,
} from "@Light/services/lightTypes";
import { useState } from "react";
import { useEnrollment } from "../flow/context";
import { useIsAfterRate } from "../flow/useIsAfterRate";
import { useIsReEnrollment } from "../useIsReEnrollment";
import { EnrollRoute } from "../routes";
import { addressResponseToRequirementsPatch } from "./patch";
import { useAddressSearch, type UseAddressSearch } from "./useAddressSearch";
import {
  useUtilityAccountNumberSearch,
  type UseUtilityAccountNumberSearch,
} from "./useUtilityAccountNumberSearch";

export type ServiceLocationSheet =
  "address" | "uan" | "alias" | "utility" | "unallowed-address-change";

export type UseServiceLocationSheetsOptions = {
  postalCode?: string;
  /** Extra requirements to merge into the patch when an address is submitted. */
  additionalRequirements?: RequirementsPatch;
  /** Whether address search is offered as a sheet. */
  addressSearchSheet?: boolean;
  /** Whether ESI ID entry is offered as a sheet. */
  utilityAccountNumberSheet?: boolean;
};

export type UseServiceLocationSheets = {
  addressSearch: UseAddressSearch;
  uanSearch: UseUtilityAccountNumberSearch;
  activeSheet?: ServiceLocationSheet;
  openSheet: (sheet: "address" | "uan") => void;
  closeSheet: () => void;
  selectResult: (result: AddressSearchResponse) => void;
  pendingAddress?: AddressSearchResponse;
  confirmAlias: () => void;
  confirmUtility: () => void;
  dismissConfirmation: () => void;
  addressSearchSheet: boolean;
  utilityAccountNumberSheet: boolean;
};

/** Hook that powers `ServiceLocationSheets` */
export function useServiceLocationSheets({
  postalCode,
  additionalRequirements,
  addressSearchSheet = true,
  utilityAccountNumberSheet = true,
}: UseServiceLocationSheetsOptions = {}): UseServiceLocationSheets {
  const { requirements, next, save, navigate } = useEnrollment();
  const isAfterRate = useIsAfterRate();
  const isReEnrollment = useIsReEnrollment();
  const [activeSheet, setActiveSheet] = useState<ServiceLocationSheet>();
  const [pendingAddress, setPendingAddress] = useState<
    AddressSearchResponse | undefined
  >();

  const quotedUtility: string | undefined = isAfterRate
    ? requirements.utility?.value?.utility?.id
    : undefined;

  const addressSearch = useAddressSearch({ postalCode });
  const uanSearch = useUtilityAccountNumberSearch({
    onFound: (result) => {
      setPendingAddress(result);
      confirmUtilityOrSubmit(result);
    },
  });

  const openSheet = (next: "address" | "uan") => {
    if (next === "address" && isReEnrollment) {
      setActiveSheet("unallowed-address-change");
      return;
    }
    if (next === "address") {
      addressSearch.reset();
    } else {
      uanSearch.reset();
    }
    setActiveSheet(next);
  };

  function selectResult(result: AddressSearchResponse) {
    setPendingAddress(result);
    if (result.alias) {
      captureEvent("enroll_address_alias_selected");
      setActiveSheet("alias");
      return;
    }
    confirmUtilityOrSubmit(result);
  }

  function confirmUtilityOrSubmit(result: AddressSearchResponse) {
    if (quotedUtility && result.utility && result.utility !== quotedUtility) {
      captureEvent("enroll_address_utility_change_prompted");
      setActiveSheet("utility");
      return;
    }
    void submit(result, false);
  }

  async function submit(
    result: AddressSearchResponse,
    utilityChanged: boolean,
  ) {
    const patch = {
      ...addressResponseToRequirementsPatch(result),
      ...additionalRequirements,
    };
    if (utilityChanged) {
      const updated = await save(patch);
      if (updated === undefined) {
        return; // Display handled with `saveMutation`
      }
      // A changed utility re-quotes the rate; record the confirm screen as the
      // return point so accepting the re-quote advances forward while revisiting
      // the address confirms it rather than searching again.
      navigate(EnrollRoute.ConfirmRate, {
        returnTo: EnrollRoute.ConfirmAddress,
      });
      return;
    }
    // Record the confirm screen as the return point so revisiting the flow
    // confirms the address the customer provided instead of searching again.
    await next(patch, { returnTo: EnrollRoute.ConfirmAddress });
  }

  return {
    addressSearch,
    uanSearch,
    activeSheet,
    pendingAddress,
    openSheet,
    closeSheet: () => {
      setActiveSheet(undefined);
    },
    selectResult,
    confirmAlias: () => {
      captureEvent("enroll_address_alias_confirmed");
      if (pendingAddress) confirmUtilityOrSubmit(pendingAddress);
    },
    confirmUtility: () => {
      captureEvent("enroll_address_utility_change_confirmed");
      if (pendingAddress) void submit(pendingAddress, true);
    },
    dismissConfirmation: () => {
      captureEvent(
        activeSheet === "utility"
          ? "enroll_address_utility_change_dismissed"
          : "enroll_address_alias_dismissed",
      );
      setActiveSheet(addressSearchSheet ? "address" : undefined);
    },
    addressSearchSheet,
    utilityAccountNumberSheet,
  };
}
